import type { useKagami } from "../state/useKagami";
import { Chip, mutedColor } from "./primitives";

type K = ReturnType<typeof useKagami>;

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 256 256" fill="currentColor"
    style={{ position: "absolute", left: 10, opacity: 0.45 }} aria-hidden="true">
    <path d="M229.7 218.3l-43.3-43.3a92.2 92.2 0 1 0-11.4 11.4l43.3 43.3a8 8 0 0 0 11.4-11.4zM40 116a76 76 0 1 1 76 76 76.1 76.1 0 0 1-76-76z" />
  </svg>
);

export function Header({ k }: { k: K }) {
  return (
    <header style={{
      position: "sticky", top: 0, zIndex: 50,
      background: "color-mix(in srgb, var(--color-bg) 90%, transparent)",
      backdropFilter: "blur(12px)", borderBottom: "1px solid var(--color-divider)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 20px", maxWidth: 1560, margin: "0 auto" }}>
        <div style={{ fontFamily: "var(--font-heading)", fontWeight: 600, fontSize: 16, letterSpacing: ".18em", flex: "none" }}>
          KAGAMI
        </div>

        <div style={{ display: "flex", gap: 4, flex: "none" }}>
          <Chip on={k.kind === "anime"} onClick={() => k.switchKind("anime")}>Anime</Chip>
          <Chip on={k.kind === "manga"} onClick={() => k.switchKind("manga")}>Manga</Chip>
        </div>

        <div style={{ flex: 1, minWidth: 0, position: "relative", display: "flex", alignItems: "center" }}>
          <SearchIcon />
          <input className="input" placeholder="Search" value={k.filters.query} aria-label="Search the catalog"
            onChange={(e) => k.setFilters((f) => ({ ...f, query: e.target.value }))}
            style={{ paddingLeft: 32, borderRadius: 999 }} />
        </div>

        <button onClick={() => k.setProfileOpen(true)} style={{
          flex: "none", display: "flex", alignItems: "center", gap: 8, padding: "3px 10px 3px 3px",
          borderRadius: 999, border: "1px solid var(--color-divider)", background: "transparent",
          color: "inherit", fontFamily: "inherit", cursor: "pointer",
        }}>
          <span style={{
            width: 28, height: 28, borderRadius: "50%", display: "grid", placeItems: "center",
            fontFamily: "var(--font-heading)", fontWeight: 600, fontSize: 11,
            background: "var(--color-accent-800)", color: "var(--color-accent-100)",
          }}>RK</span>
          <span className="k-desk" style={{ fontSize: 13 }}>Rin Kadowaki</span>
        </button>
      </div>

      <div className="k-desk" style={{ display: "flex", alignItems: "center", gap: 18, padding: "0 20px 10px", maxWidth: 1560, margin: "0 auto" }}>
        <nav style={{ display: "flex", gap: 16 }}>
          <Chip on={k.section === "discover"} onClick={() => k.setSection("discover")} style={navChip}>Discover</Chip>
          <Chip on={k.section === "library"} onClick={() => k.setSection("library")} style={navChip}>
            Library ({k.libraryWorks.length})
          </Chip>
          <Chip on={k.section === "boards"} onClick={() => k.setSection("boards")} style={navChip}>Leaderboards</Chip>
          <Chip on={k.section === "identify"} onClick={() => k.setSection("identify")} style={navChip}>Identify</Chip>
        </nav>
      </div>

      {/* Says out loud that the numbers below are demo data. A silent fallback
          would be indistinguishable from a broken recommender — ADR-0006. */}
      {k.mode === "fixture" && !k.loading && (
        <div className="k-fixture-banner" role="status">
          <strong>Demo data.</strong>
          <span>Showing the design's {k.catalog.length}-title fixture catalog because {k.reason}.</span>
        </div>
      )}
    </header>
  );
}

const navChip = { border: 0, fontSize: 13, padding: "2px 0", color: mutedColor(68) } as const;
